import type { AuthUser } from "../../types/authUser";

/** Organization Admins and Team Admins can send to Everyone; other Team members can't. */
export const canAccessMainCircle = (user: AuthUser | null | undefined): boolean => {
  if (!user?.userType || user.userType !== "team") {
    return true;
  }
  return user.accessLevel?.toLowerCase() === "admin";
};

/**
 * The Circles this person may send to: every Circle for Admins, and only
 * their own (`circleIds`) for other Team members.
 */
export function accessibleSubcircles<C extends { circleId: string; isSubCircle: boolean }>(
  circles: C[],
  user: AuthUser | null | undefined,
): C[] {
  const subcircles = circles.filter((circle) => circle.isSubCircle);
  if (!user?.userType || user.userType !== "team") {
    return subcircles;
  }
  if (user.accessLevel?.toLowerCase() === "admin") {
    return subcircles;
  }
  const allowedIds = user.circleIds || [];
  if (!Array.isArray(allowedIds) || allowedIds.length === 0) {
    return [];
  }
  return subcircles.filter((circle) => allowedIds.includes(circle.circleId));
}

/**
 * The Circles a new User can be invited into: every Circle except the
 * Organization's own, and only their own (`circleIds`) for Team members.
 */
export function inviteCircles<C extends { circleId: string }>(
  circles: C[],
  user: AuthUser | null | undefined,
): C[] {
  const subcircles = circles.filter((circle) => circle.circleId !== user?.schoolId);
  if (user?.userType !== "team") {
    return subcircles;
  }
  const allowed = user?.circleIds;
  if (!Array.isArray(allowed) || allowed.length === 0) {
    return [];
  }
  const allow = new Set(allowed.map(String));
  return subcircles.filter((circle) => allow.has(circle.circleId));
}
